import {
  alpha,
  Box,
  BoxProps,
  styled,
  Theme,
  Typography,
  TypographyProps
} from "@mui/material"
import { colorPalette } from "../../../ui/containers/AlertContainer/helpers"
import { focusStyle } from "../../../theme"
import { Variant } from "../../../types"

type SimpleChoiceProps = BoxProps & {
  isFocused?: boolean
  isReviewMode?: boolean
  disabled?: boolean
  isCorrect?: boolean
  variant?: Variant
}

const STYLED_PROPS = [
  "isFocused",
  "isStrikethrough",
  "isReviewMode",
  "disabled",
  "variant"
]

const commonAfterStyles = (theme: Theme) => ({
  textAlign: "center",
  position: "absolute",
  right: theme.spacing(2.625),
  top: "50%",
  borderRadius: "100px",
  backgroundColor: theme.palette.common.white,
  border: "1px solid",
  fontSize: "12px",
  lineHeight: "14px",
  fontWeight: 400,
  padding: theme.spacing(0.5, 1.5),
  transform: "translate(0, -50%)"
})

export const SimpleChoiceWrapper = styled(Box, {
  shouldForwardProp: prop => !STYLED_PROPS.includes(prop as string)
})<SimpleChoiceProps>(
  ({
    theme,
    isFocused = false,
    isReviewMode = false,
    disabled = false,
    variant
  }) => ({
    padding: theme.spacing(3),
    userSelect: "none",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    borderRadius: `${theme.shape.borderRadius * 1.5}px`,
    border: `1px solid ${theme.palette.blue[100]}`,

    ...(isFocused && {
      ...focusStyle(theme).border
    }),
    ...(isReviewMode || disabled || variant
      ? {
          border: `1px solid ${theme.palette.grey[100]}`
        }
      : {
          cursor: "pointer",

          "&:hover": {
            backgroundColor: theme.palette.blue[50],
            border: `1px solid ${theme.palette.blue[50]}`,

            [theme.breakpoints.down("mobile")]: {
              ...focusStyle(theme).border
            }
          },

          ":focus-within": {
            ...focusStyle(theme).border
          }
        }),

    ...(disabled &&
      !isReviewMode && {
        [`& ${ContentWrapper}`]: {
          color: theme.palette.grey[500]
        }
      }),

    ...(variant &&
      variant === Variant.correct && {
        backgroundColor: theme.palette.lightGreen[700],
        position: "relative",
        "&:after": {
          ...commonAfterStyles(theme),
          content: `"Correct"`,
          color: theme.palette.lightGreen[700],
          borderColor: theme.palette.lightGreen[300]
        }
      }),
    ...(variant &&
      variant === Variant.invalid && {
        backgroundColor: theme.palette.orange[700],
        position: "relative",
        "&:after": {
          ...commonAfterStyles(theme),
          content: `"Incorrect"`,
          color: theme.palette.orange[700],
          borderColor: theme.palette.orange[300]
        }
      }),
    ...(variant &&
      variant === Variant.neutral && {
        backgroundColor: theme.palette.blue["A800"],
        position: "relative",
        "&:after": {
          ...commonAfterStyles(theme),
          color: theme.palette.blue["A800"],
          borderColor: theme.palette.blue[300]
        }
      }),

    [theme.breakpoints.down("mobile")]: {
      padding: theme.spacing(2)
    }
  })
)

export const BubbleWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== "blankBubble"
})<
  BoxProps & {
    blankBubble?: boolean
  }
>(({ blankBubble = false }) => ({
  ...(blankBubble && {
    visibility: "hidden"
  })
}))

export const ContentWrapper = styled(Box)<{
  isStrikethrough?: boolean
  variant?: Variant
}>(({ theme, isStrikethrough = false, variant }) => ({
  width: "100%",
  maxWidth: variant ? "calc(100% - 125px)" : "100%",
  color: theme.palette.grey[800],
  fontSize: theme.typography.body1.fontSize,
  lineHeight: theme.typography.body1.lineHeight,
  marginLeft: theme.spacing(6),
  position: "relative",

  ...(isStrikethrough && {
    color: alpha(theme.palette.grey[800], 0.6),

    "& > div": {
      opacity: 0.6
    }
  }),

  [`& .react-transform-wrapper > .react-transform-component`]: {
    justifyContent: "flex-start !important"
  },

  ...(isStrikethrough && {
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: "100%",
      height: "2px",
      backgroundColor: theme.palette.grey[400],
      zIndex: 1
    }
  }),

  // Target text color for rich-text after answer variant is applied
  "& p": {
    marginBottom: "0.125rem !important",
    ...(variant && {
      color: theme.palette.common.white
    })
  },

  // Target text color for math & text after answer variant is applied
  "& span": {
    ...(variant && {
      color: theme.palette.common.white
    })
  },

  [theme.breakpoints.down("mobile")]: {
    marginLeft: theme.spacing(4.25)
  }
}))

export const PercentageLabel = styled(Typography)<
  TypographyProps & { correct?: boolean }
>(({ theme, correct }) => ({
  fontWeight: 500,
  color: colorPalette(theme, correct ? "success" : "warning").color,
  whiteSpace: "pre",
  marginRight: theme.spacing(4)
}))
